import { FunctionComponent, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { Country, PaymentType } from '@afterpay/types'
import { useFlag } from '@afterpaytouch/feature'
import { Cohort } from '@afterpaytouch/portal-api/types'
import { Route } from '../../config/router'
import { CheckoutStatus } from '../../model/checkout'
import { isCountryNeedsBiometricVerification, isCountryUsePersonaFlow } from '../../model/country'
import { Features } from '../../model/features'
import { useCheckoutError, useCheckoutState, useCheckoutStatus, useCohort, useCountry, useIsInlinePaymentOptions, useSelectedPaymentType } from '../../state'
import { isAppleRetailOrder } from '../../state/checkout/helper'
import { isCashGrantHandoffPending } from './cashAuthJourney'
import {
  IDENTITY_VERIFICATION_SIGNALS,
  USER_JOURNEY_NAMES,
  USER_JOURNEY_TIMEOUT_LATE_FIRE_GRACE_MS,
  USER_JOURNEY_TIMEOUT_MS,
  USER_JOURNEY_VARIANTS,
} from './constants'
import { useJourneyNetworkSignals } from './networkSignals'
import { onBeforeRumSessionStop } from './sessionStopSignal'
import { mapTerminalErrorToJourney } from './terminalErrorMapping'
import { JourneyOutcome, JourneyTerminationReason } from './types'
import { useUserJourneyContext } from './UserJourneyContext'

const COHORT_TAGS: Record<Cohort, string> = {
  [Cohort.CashOnly]: 'cash-only',
  [Cohort.APOnly]: 'ap-only',
  [Cohort.NetNew]: 'net-new',
}

export const useStartCheckoutJourney = (): void => {
  const enabled = useFlag(Features.USER_JOURNEY_INSTRUMENTATION)
  const { startJourney } = useUserJourneyContext()
  const started = useRef(false)

  useEffect(() => {
    if (!enabled || started.current) {
      return
    }
    started.current = true
    startJourney(USER_JOURNEY_NAMES.CHECKOUT)
  }, [enabled, startJourney])
}

export const useCompleteCheckoutJourneyOnTerminalError = (): void => {
  const { active, addFriction, addFrustration, completeJourney } = useUserJourneyContext()
  const checkoutError = useCheckoutError()

  useEffect(() => {
    if (active?.name !== USER_JOURNEY_NAMES.CHECKOUT) {
      return
    }
    const { outcome, signalType, signal } = mapTerminalErrorToJourney(checkoutError?.error, checkoutError?.errorCode)
    if (signalType === 'friction') {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, signal)
    } else {
      addFrustration(USER_JOURNEY_NAMES.CHECKOUT, signal)
    }
    completeJourney(USER_JOURNEY_NAMES.CHECKOUT, outcome)
  }, [active, checkoutError, addFriction, addFrustration, completeJourney])
}

const idvTypeFromPathname = (pathname: string, country: Country | null | undefined): string | undefined => {
  if (pathname.includes(Route.VERIFY_RETRY)) {
    return 'profile-retry'
  }
  if (!pathname.includes(Route.VERIFY_IDENTITY)) {
    return undefined
  }
  if (pathname.includes(Route.VERIFY_ADDRESS)) {
    return 'profile-retry'
  }
  if (pathname.includes(Route.VERIFY_BIOMETRIC_PERSONA)) {
    return 'persona'
  }
  if (pathname.includes('/biometric') || pathname.includes(Route.VERIFY_IDENTITY_START)) {
    if (country == null) {
      return undefined
    }
    return isCountryUsePersonaFlow(country) ? 'persona' : 'biometric'
  }
  if (country == null) {
    return undefined
  }
  if (isCountryUsePersonaFlow(country)) {
    return 'persona'
  }
  return isCountryNeedsBiometricVerification(country) ? 'biometric' : 'document'
}

export const CheckoutJourneyAnchors: FunctionComponent = () => {
  const { active, addFriction, addTag, completeJourney, setVariant } = useUserJourneyContext()
  useJourneyNetworkSignals()
  const status = useCheckoutStatus()
  const cohort = useCohort()
  const country = useCountry()
  const paymentType = useSelectedPaymentType()
  const isInlinePaymentOptions = useIsInlinePaymentOptions()
  const { initial, checkout, started } = useCheckoutState()
  const { pathname } = useRouter()

  const journeyActive = active?.name === USER_JOURNEY_NAMES.CHECKOUT

  const planVariant = isAppleRetailOrder(checkout?.merchant.source)
    ? USER_JOURNEY_VARIANTS.APPLE_RETAIL
    : paymentType === PaymentType.PCL
    ? USER_JOURNEY_VARIANTS.PAY_MONTHLY
    : started?.paymentSchedules?.length === 2
    ? USER_JOURNEY_VARIANTS.PAY_IN_2
    : started?.paymentSchedules?.length === 4
    ? USER_JOURNEY_VARIANTS.PAY_IN_4
    : undefined

  useEffect(() => {
    if (journeyActive && active !== undefined && planVariant !== undefined && active.variant !== planVariant) {
      setVariant(USER_JOURNEY_NAMES.CHECKOUT, planVariant)
    }
  }, [journeyActive, active, planVariant, setVariant])

  useEffect(() => {
    if (!journeyActive || active === undefined) {
      return
    }
    const tagOnce = (key: string, value: string | number | null | undefined): void => {
      if (value == null || value === '' || active.tags.some((tag) => tag.startsWith(`${key}:`))) {
        return
      }
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `${key}:${value}`)
    }
    tagOnce('merchantId', checkout?.merchant.id)
    tagOnce('country', country)
    tagOnce('windowType', !initial?.token ? undefined : initial.isIframe ? 'iframe' : initial.isWindowed ? 'popup' : 'redirect')
    tagOnce('cohort', cohort === undefined ? undefined : COHORT_TAGS[cohort])
    tagOnce('idvType', idvTypeFromPathname(pathname, country))
  }, [journeyActive, active, addTag, checkout, cohort, country, initial, pathname])

  useEffect(() => {
    if (!journeyActive || active === undefined || status !== CheckoutStatus.IdentityIneligible) {
      return
    }
    if (idvTypeFromPathname(pathname, country) === undefined) {
      return
    }
    if (!active.frictions.includes(IDENTITY_VERIFICATION_SIGNALS.REQUIRED)) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, IDENTITY_VERIFICATION_SIGNALS.REQUIRED)
    }
  }, [journeyActive, active, status, addFriction, pathname, country])

  useEffect(() => {
    if (!journeyActive || active === undefined || paymentType !== PaymentType.PCL) {
      return
    }
    if (isInlinePaymentOptions || !pathname.includes(Route.PAYMENT_OPTIONS)) {
      return
    }
    if (!active.tags.some((tag) => tag.startsWith('paymentType:'))) {
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `paymentType:${PaymentType.PCL}`)
    }
    completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.SUCCEEDED)
  }, [journeyActive, active, paymentType, pathname, isInlinePaymentOptions, addTag, completeJourney])

  useEffect(() => {
    if (!journeyActive || active === undefined || status !== CheckoutStatus.Confirmed) {
      return
    }
    if (!active.tags.some((tag) => tag.startsWith('paymentType:'))) {
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `paymentType:${paymentType ?? PaymentType.PBI}`)
    }
    completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.SUCCEEDED)
  }, [journeyActive, active, status, paymentType, addTag, completeJourney])

  useEffect(() => {
    if (!journeyActive) {
      return undefined
    }
    const recordProcessKilled = (): void => {
      if (isCashGrantHandoffPending()) {
        return
      }
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.PROCESS_KILLED)
    }
    const unsubscribe = onBeforeRumSessionStop(recordProcessKilled)
    window.addEventListener('pagehide', recordProcessKilled)
    return () => {
      unsubscribe()
      window.removeEventListener('pagehide', recordProcessKilled)
    }
  }, [journeyActive, completeJourney])

  const activeJourneyStarted = active?.created
  useEffect(() => {
    if (!journeyActive || activeJourneyStarted === undefined) {
      return undefined
    }
    const deadline = activeJourneyStarted + USER_JOURNEY_TIMEOUT_MS
    const remaining = Math.max(0, deadline - Date.now())
    const timer = window.setTimeout(() => {
      const firedLate = Date.now() - deadline > USER_JOURNEY_TIMEOUT_LATE_FIRE_GRACE_MS
      const reason =
        firedLate || document.visibilityState === 'hidden' ? JourneyTerminationReason.TIMEOUT_WITH_IDLE_USER : JourneyTerminationReason.TIMEOUT_WITH_ACTIVE_USER
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, reason)
    }, remaining)
    return () => {
      window.clearTimeout(timer)
    }
  }, [journeyActive, activeJourneyStarted, completeJourney])

  return null
}
